"use client";

import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { PageHeader } from "@/components/PageHeader";
import { useOpenClaw } from "@/lib/useOpenClaw";
import {
  PRIORITY_DOT_COLORS,
  PILL_BASE,
  STATUS_ACCENT,
  STATUS_PILL,
  STATUS_TEXT,
  type StatusKey,
  formatRelativeTime,
  formatDate,
  isStale,
  cn,
} from "@/lib/utils";
import { AlertTriangle, Clock, CheckSquare, Users, Radio, Zap, ArrowRight, Wifi, WifiOff, Link2 } from "lucide-react";
import Link from "next/link";
import { useClientContext } from "@/context/ClientContext";

// Card lists sit in a recessed well so the cards read as floating, not stacked.
// gap-2 rather than space-y-2 on purpose: most of these lists wrap each card in
// a bare <Link>, which renders as an inline <a>, and vertical margins don't
// apply to inline boxes — space-y-* silently collapsed to nothing. Flex
// blockifies its children, so the gap actually lands.
const CARD_LIST = "flex flex-col gap-2 rounded-xl bg-muted/35 p-2 dark:bg-zinc-900/30";

function StatCard({
  label,
  value,
  sub,
  accent,
  href,
  live,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: StatusKey;
  href?: string;
  live?: boolean;
}) {
  const card = (
    <div
      className={cn(
        "rounded-xl border border-border bg-card p-5 shadow-sm transition-colors",
        // State lives in the left bar only — the card stays neutral.
        accent && `border-l-4 ${STATUS_ACCENT[accent]}`,
        href && "cursor-pointer hover:border-primary/20 hover:bg-muted/40"
      )}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        {live && <div className="w-1.5 h-1.5 rounded-full bg-status-done animate-pulse" />}
      </div>
      <div className={cn("text-3xl font-bold tabular-nums", accent ? STATUS_TEXT[accent] : "text-foreground")}>
        {value}
      </div>
      {sub && <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );

  if (href) return <Link href={href}>{card}</Link>;
  return card;
}

export default function DashboardPage() {
  const { clientId, currentClient } = useClientContext();
  const allTasks = useQuery(api.tasks.list);
  const team = useQuery(api.team.list);
  const channels = useQuery(api.channels.list);
  const activity = useQuery(api.activity.listRecent, { limit: 10 });
  const scheduled = useQuery(api.scheduled.listActive);
  const projects = useQuery(api.projects.list);
  const { data: oc } = useOpenClaw();

  if (!allTasks || !team || !channels || !activity || !scheduled || !projects) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="rounded-lg border border-dashed border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm">Loading dashboard…</div>
      </div>
    );
  }

  // When a client is selected, scope tasks to that client's projects
  const clientProjectIds = clientId
    ? new Set(projects.filter((p) => p.clientId === clientId).map((p) => p._id))
    : null;
  const tasks = clientProjectIds
    ? allTasks.filter((t) => t.projectId && clientProjectIds.has(t.projectId))
    : allTasks;

  const dependencyMap = new Map(allTasks.map((task) => [task._id, task]));
  const blockedTasks = tasks.filter((t) => Boolean(t.blockedReason));
  const autoBlockedTasks = blockedTasks.filter((t) => t.blockedReason?.startsWith("Dependency blocked:"));
  const staleTasks = tasks.filter((t) => isStale(t.updatedAt, t.status));
  const inboxTasks = tasks.filter((t) => t.status === "inbox");
  const reviewTasks = tasks.filter((t) => t.status === "review");
  const activeTasks = tasks.filter((t) => t.status === "working");
  const readyTasks = tasks.filter((t) => t.status === "ready");
  const activeChannels = channels.filter((c) => c.status === "active");
  const activeAgents = team.filter((m) => m.status !== "inactive");
  const upcomingScheduled = scheduled
    .filter((s) => s.nextRunAt)
    .sort((a, b) => (a.nextRunAt ?? 0) - (b.nextRunAt ?? 0))
    .slice(0, 4);
  const assigneePriority = activeAgents
    .map((member) => {
      const assignedTasks = tasks.filter((task) => task.assigneeId === member._id && task.status !== "done");
      const current = assignedTasks
        .filter((task) => task.status === "working")
        .sort((a, b) => (a.sortOrder ?? a.createdAt) - (b.sortOrder ?? b.createdAt))[0];
      const queue = assignedTasks
        .filter((task) => task.status === "ready" || task.status === "inbox")
        .sort((a, b) => {
          const statusRank = (taskStatus: string) => (taskStatus === "ready" ? 0 : 1);
          return statusRank(a.status) - statusRank(b.status) || (a.sortOrder ?? a.createdAt) - (b.sortOrder ?? b.createdAt);
        });
      const next = queue[0];
      const blocked = assignedTasks.filter((task) => Boolean(task.blockedReason)).length;
      return {
        member,
        current,
        next,
        queue: queue.slice(1, 3),
        blocked,
        total: assignedTasks.length,
      };
    })
    .filter((entry) => entry.current || entry.next || entry.queue.length > 0)
    .sort((a, b) => (b.current ? 1 : 0) - (a.current ? 1 : 0) || (b.total - a.total));

  // Real OpenClaw data where available
  const ocAgentCount = oc?.agents.length ?? null;
  const ocChannelCount = oc?.channels.length ?? null;
  const ocCronCount = oc?.cronJobs.length ?? null;
  const gatewayUp = oc?.gateway.running ?? false;

  // Derive real task state from events.jsonl
  const ocTaskEvents = oc?.tasks ?? [];
  // Get latest state per task
  const ocTaskStates = new Map<string, { state: string; summary: string; ts: string }>();
  for (const event of ocTaskEvents) {
    if (event.state) {
      ocTaskStates.set(event.taskId, { state: event.state, summary: event.summary, ts: event.ts });
    }
  }
  const ocWorkingTasks = [...ocTaskStates.values()].filter((t) => t.state === "working");
  const ocReviewTasks = [...ocTaskStates.values()].filter((t) => t.state === "review_ready");

  return (
    <div className="p-0">
      <PageHeader
        title={currentClient ? currentClient.name : "Dashboard"}
        description={currentClient ? `${currentClient.name} · Dashboard` : "Operational overview across Backchannel and OpenClaw"}
        actions={
          <div className="flex items-center gap-3">
            {/* Gateway status */}
            <div className="flex items-center gap-1.5">
              {gatewayUp ? (
                <Wifi className="w-3.5 h-3.5 text-status-done" />
              ) : (
                <WifiOff className="w-3.5 h-3.5 text-muted-foreground" />
              )}
              <span className={cn("text-xs", gatewayUp ? STATUS_TEXT.done : "text-muted-foreground")}>
                Gateway {gatewayUp ? "up" : "down"}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-status-done animate-pulse" />
              <span className="text-xs text-muted-foreground">Live</span>
            </div>
          </div>
        }
      />

      <div className="p-8 space-y-8">
        {/* Attention row — things that need action */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Blocked"
            value={blockedTasks.length}
            sub="need attention"
            accent={blockedTasks.length > 0 ? "blocked" : "inbox"}
            href="/tasks?filter=blocked"
          />
          <StatCard
            label="Stale"
            value={staleTasks.length}
            sub="no update in 3+ days"
            accent={staleTasks.length > 0 ? "waiting" : "inbox"}
            href="/tasks?filter=stale"
          />
          <StatCard
            label="Review"
            value={reviewTasks.length}
            sub={ocReviewTasks.length > 0 ? `${ocReviewTasks.length} in OpenClaw` : "awaiting review"}
            accent={reviewTasks.length > 0 ? "review" : "inbox"}
            href="/tasks?filter=review"
          />
          <StatCard
            label="Inbox"
            value={inboxTasks.length}
            sub={autoBlockedTasks.length > 0 ? `${autoBlockedTasks.length} waiting on dependencies` : "unstarted"}
            accent={readyTasks.length > 0 ? "ready" : "inbox"}
            href="/tasks?filter=inbox"
          />
        </div>

        {/* Operational status row — real data where available */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Agents"
            value={ocAgentCount ?? activeAgents.length}
            sub={ocAgentCount !== null ? "from OpenClaw config" : `${activeAgents.length} online`}
            accent="working"
            href="/office"
            live={ocAgentCount !== null}
          />
          <StatCard
            label="Channels"
            value={ocChannelCount ?? activeChannels.length}
            sub={ocChannelCount !== null ? "Telegram accounts" : `${channels.length} total`}
            accent="done"
            href="/channels"
            live={ocChannelCount !== null}
          />
          <StatCard
            label="Working"
            value={activeTasks.length}
            sub={ocWorkingTasks.length > 0 ? `${ocWorkingTasks.length} tracked in OpenClaw` : `${tasks.length} total tasks`}
            accent="working"
            href="/tasks"
          />
          <StatCard
            label="Cron Jobs"
            value={ocCronCount ?? scheduled.length}
            sub={ocCronCount !== null
              ? (ocCronCount === 0 ? "none configured" : "from OpenClaw config")
              : "from Backchannel"}
            href="/calendar"
            live={ocCronCount !== null}
          />
        </div>

        {/* Main content area */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Tasks that need attention */}
          <div className="space-y-6">
            {/* Blocked tasks */}
            {blockedTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-status-blocked" />
                  <span className={cn("text-sm font-medium", STATUS_TEXT.blocked)}>Blocked</span>
                  <span className="text-xs text-muted-foreground">{blockedTasks.length}</span>
                </div>
                <div className={CARD_LIST}>
                  {blockedTasks.map((task) => {
                    const assignee = team.find((m) => m._id === task.assigneeId);
                    return (
                      <Link key={task._id} href={`/tasks/${task._id}`}>
                        <div className={cn(
                          "flex items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/30 border-l-4",
                          STATUS_ACCENT.blocked
                        )}>
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                              PRIORITY_DOT_COLORS[task.priority]
                            )}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium text-foreground">
                              {task.title}
                            </div>
                            {task.blockedReason?.startsWith("Dependency blocked:") ? (
                              <div className={cn("mt-1.5", PILL_BASE, STATUS_PILL.waiting)}>
                                <Link2 className="h-3 w-3" /> {task.blockedReason.replace("Dependency blocked: ", "")}
                              </div>
                            ) : (
                              <div className={cn("mt-1.5", PILL_BASE, STATUS_PILL.blocked)}>Blocked</div>
                            )}
                            {task.notes && (
                              <div className="mt-1 truncate text-xs text-muted-foreground">
                                {task.notes}
                              </div>
                            )}
                          </div>
                          {assignee && (
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                              style={{ backgroundColor: assignee.avatarColor }}
                            >
                              {assignee.name[0]}
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stale tasks */}
            {staleTasks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-status-waiting" />
                  <span className={cn("text-sm font-medium", STATUS_TEXT.waiting)}>Stale</span>
                  <span className="text-xs text-muted-foreground">{staleTasks.length}</span>
                </div>
                <div className={CARD_LIST}>
                  {staleTasks.map((task) => {
                    const assignee = team.find((m) => m._id === task.assigneeId);
                    return (
                      <Link key={task._id} href={`/tasks/${task._id}`}>
                        <div className={cn(
                          "flex items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/30 border-l-4",
                          STATUS_ACCENT.waiting
                        )}>
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                              PRIORITY_DOT_COLORS[task.priority]
                            )}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium text-foreground">
                              {task.title}
                            </div>
                            <div className={cn("mt-1.5", PILL_BASE, STATUS_PILL.waiting)}>Stale</div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Last updated {formatRelativeTime(task.updatedAt)}
                            </div>
                          </div>
                          {assignee && (
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                              style={{ backgroundColor: assignee.avatarColor }}
                            >
                              {assignee.name[0]}
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active tasks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-status-working" />
                  <span className="text-sm font-medium text-foreground">Working</span>
                </div>
                <Link
                  href="/tasks"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  All tasks <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className={CARD_LIST}>
                {activeTasks.filter((t) => !isStale(t.updatedAt, t.status)).map((task) => {
                  const assignee = team.find((m) => m._id === task.assigneeId);
                  const project = projects.find((p) => p._id === task.projectId);
                  return (
                    <Link key={task._id} href={`/tasks/${task._id}`}>
                      <div className={cn(
                        "flex items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/30 border-l-4",
                        STATUS_ACCENT.working
                      )}>
                        <div
                          className={cn(
                            "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                            PRIORITY_DOT_COLORS[task.priority]
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm text-foreground">{task.title}</div>
                          {project && (
                            <div className="mt-1 truncate text-xs text-muted-foreground">
                              {project.title}
                            </div>
                          )}
                          {(task.dependencyIds ?? []).some((dependencyId) => {
                            const dependency = dependencyMap.get(dependencyId);
                            return dependency && dependency.status !== "done";
                          }) && (
                            <div className={cn("mt-1.5", PILL_BASE, STATUS_PILL.waiting)}>
                              <Link2 className="h-3 w-3" /> Waiting on dependency
                            </div>
                          )}
                        </div>
                        {assignee && (
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: assignee.avatarColor }}
                          >
                            {assignee.name[0]}
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
                {activeTasks.filter((t) => !isStale(t.updatedAt, t.status)).length === 0 && (
                  <div className="rounded-lg border border-dashed border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">No active tasks</div>
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Team status — OpenClaw-backed */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Team</span>
                  {oc && <div className="w-1.5 h-1.5 rounded-full bg-status-done animate-pulse" />}
                </div>
                <Link
                  href="/office"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  View all <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className={CARD_LIST}>
                {oc ? (
                  // Real agents from OpenClaw
                  oc.agents.map((agent) => (
                    <div
                      key={agent.id}
                      className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 shadow-sm dark:border-zinc-800/60 dark:bg-zinc-900/60"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 bg-primary"
                      >
                        {agent.emoji || agent.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">{agent.name}</div>
                        <div className="text-xs text-muted-foreground">{agent.role}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {agent.telegramAccount && (
                          <span className="text-xs text-muted-foreground">TG</span>
                        )}
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          agent.isConfigured ? "bg-status-done" : "bg-status-inbox"
                        )} />
                      </div>
                    </div>
                  ))
                ) : (
                  // Fallback to Convex data
                  activeAgents.map((member) => (
                    <div
                      key={member._id}
                      className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 shadow-sm dark:border-zinc-800/60 dark:bg-zinc-900/60"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: member.avatarColor }}
                      >
                        {member.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">{member.name}</div>
                        <div className="text-xs text-muted-foreground">{member.role}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Priority by assignee */}
            {assigneePriority.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Next Up</span>
                  </div>
                  <Link
                    href="/tasks"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Task board <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className={CARD_LIST}>
                  {assigneePriority.map(({ member, current, next, queue, blocked }) => (
                    <div
                      key={member._id}
                      className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-zinc-800/60 dark:bg-zinc-900/60"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: member.avatarColor }}
                        >
                          {member.name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-foreground">{member.name}</div>
                            {blocked > 0 && (
                              <span className={cn(PILL_BASE, STATUS_PILL.waiting)}>
                                <Link2 className="h-3 w-3" /> {blocked} waiting
                              </span>
                            )}
                          </div>
                          <div className="mt-2 space-y-1.5 text-xs">
                            <div>
                              <span className="text-muted-foreground">Current:</span>{" "}
                              {current ? (
                                <Link href={`/tasks/${current._id}`} className="text-foreground hover:underline">
                                  {current.title}
                                </Link>
                              ) : (
                                <span className="text-muted-foreground/70">—</span>
                              )}
                            </div>
                            <div>
                              <span className="text-muted-foreground">Next:</span>{" "}
                              {next ? (
                                <Link href={`/tasks/${next._id}`} className="text-foreground hover:underline">
                                  {next.title}
                                </Link>
                              ) : (
                                <span className="text-muted-foreground/70">No ready queue</span>
                              )}
                            </div>
                            {queue.length > 0 && (
                              <div className="text-muted-foreground">
                                Queue: {queue.map((task, index) => (
                                  <span key={task._id}>
                                    {index > 0 ? ", " : ""}
                                    <Link href={`/tasks/${task._id}`} className="text-foreground/80 hover:text-foreground">
                                      {task.title}
                                    </Link>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Channel status — OpenClaw-backed */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Channels</span>
                  {oc && <div className="w-1.5 h-1.5 rounded-full bg-status-done animate-pulse" />}
                </div>
                <Link
                  href="/channels"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  View all <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className={CARD_LIST}>
                {oc ? (
                  // Real channels from OpenClaw
                  oc.channels.map((channel) => {
                    const boundAgent = oc.agents.find((a) => a.id === channel.agentBinding);
                    return (
                      <div
                        key={channel.accountId}
                        className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 shadow-sm dark:border-zinc-800/60 dark:bg-zinc-900/60"
                      >
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-status-done" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">
                            {channel.accountId === "default" ? "Default Bot" : channel.accountId}
                          </div>
                          <div className="truncate text-xs capitalize text-muted-foreground">
                            telegram{boundAgent ? ` · ${boundAgent.name}` : ""}
                          </div>
                        </div>
                        <span className={cn(PILL_BASE, STATUS_PILL.done)}>
                          active
                        </span>
                      </div>
                    );
                  })
                ) : (
                  channels.slice(0, 4).map((channel) => {
                    const agent = team.find((m) => m._id === channel.agentId);
                    return (
                      <div
                        key={channel._id}
                        className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 shadow-sm dark:border-zinc-800/60 dark:bg-zinc-900/60"
                      >
                        <div
                          className={cn(
                            "w-2 h-2 rounded-full flex-shrink-0",
                            channel.status === "active" ? "bg-status-done" : "bg-status-inbox"
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">{channel.name}</div>
                          <div className="truncate text-xs capitalize text-muted-foreground">
                            {channel.type}{agent ? ` · ${agent.name}` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Upcoming scheduled */}
            {upcomingScheduled.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Upcoming</span>
                  </div>
                  <Link
                    href="/calendar"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Calendar <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className={CARD_LIST}>
                  {upcomingScheduled.map((item) => {
                    const assignee = team.find((m) => m._id === item.assigneeId);
                    return (
                      <div
                        key={item._id}
                        className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 shadow-sm dark:border-zinc-800/60 dark:bg-zinc-900/60"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">{item.title}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Next: {item.nextRunAt ? formatDate(item.nextRunAt) : "—"}
                            {assignee ? ` · ${assignee.name}` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* OpenClaw task events — real operational activity */}
        {ocTaskEvents.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4 text-status-working" />
              <span className="text-sm font-medium text-foreground">OpenClaw Task Activity</span>
              <div className="h-1.5 w-1.5 rounded-full bg-status-done animate-pulse" />
              <span className="font-mono text-xs text-muted-foreground">events.jsonl</span>
            </div>
            <div className="space-y-1 rounded-xl border border-border bg-card/70 p-2 shadow-sm">
              {ocTaskEvents.slice(-8).reverse().map((event, i) => (
                <div
                  key={`${event.taskId}-${event.ts}-${i}`}
                  className="flex items-start gap-3 rounded-lg px-4 py-3 transition-colors hover:bg-muted/60"
                >
                  <div className={cn(
                    "mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full",
                    event.type === "stale_detected" ? "bg-status-waiting" :
                    event.type === "state_change" ? "bg-status-done" :
                    "bg-status-working"
                  )} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground">
                      {event.type === "stale_detected" ? `Stale: ${event.title ?? event.taskId}` :
                       event.type === "state_change" ? `${event.taskId} → ${event.state}` :
                       event.summary}
                    </div>
                    {event.currentStep && (
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {event.currentStep}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-xs text-muted-foreground">
                    {new Date(event.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Convex activity feed */}
        <div>
          <div className="mb-4 flex items-center gap-2">
            <Zap className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Backchannel Activity</span>
          </div>
          <div className="space-y-1 rounded-xl border border-border bg-card/70 p-2 shadow-sm">
            {activity.map((event) => (
              <div
                key={event._id}
                className="flex items-start gap-3 rounded-lg px-4 py-3 transition-colors hover:bg-muted/60"
              >
                <div className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-status-working" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground">{event.title}</div>
                  {event.description && (
                    <div className="mt-0.5 text-xs text-muted-foreground">{event.description}</div>
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-muted-foreground">
                  {formatRelativeTime(event.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
